import { useState } from "react";
import { Search, SlidersHorizontal, X, Star, BadgeCheck, ChevronUp, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import MemberFilterModal, { type MemberFilters } from "./MemberFilterModal";

export interface MobileMemberProfile {
  id: string;
  username: string | null;
  full_name: string;
  location: string | null;
  municipality: string | null;
  province: string | null;
  avatar_url: string | null;
  account_type: "personal" | "company" | "admin" | null;
  email_verified?: boolean | null;
  company_name?: string | null;
}

interface Props {
  members: MobileMemberProfile[];
  isLoading: boolean;
  selectedMember: MobileMemberProfile | null;
  onSelectMember: (m: MobileMemberProfile | null) => void;
  search: string;
  onSearchChange: (v: string) => void;
  filters: MemberFilters;
  onFiltersChange: (f: MemberFilters) => void;
}

function getInitials(name: string) {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

const isVerified = (m: MobileMemberProfile) => !!m.email_verified;
const isCompany = (m: MobileMemberProfile) => m.account_type === "company";

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors",
        active
          ? "border-foreground bg-foreground text-background"
          : "border-border bg-card/95 text-foreground hover:bg-secondary"
      )}
    >
      {children}
    </button>
  );
}

function FloatingDetailCard({
  member,
  bottomOffset,
  onClose,
}: {
  member: MobileMemberProfile;
  bottomOffset: number;
  onClose: () => void;
}) {
  const navigate = useNavigate();

  const { data: reviews } = useQuery({
    queryKey: ["member-reviews-mobile", member.id],
    queryFn: async () => {
      const { data } = await supabase.from("purchase_reviews").select("rating").eq("target_user_id", member.id);
      return data || [];
    },
  });

  const { data: listingsCount } = useQuery({
    queryKey: ["member-listings-count-mobile", member.id],
    queryFn: async () => {
      const { count } = await supabase
        .from("listings")
        .select("*", { count: "exact", head: true })
        .eq("seller_id", member.id)
        .eq("status", "aktiv");
      return count ?? 0;
    },
  });

  const reviewCount = reviews?.length ?? 0;
  const avgRating = reviewCount > 0 ? reviews!.reduce((s, r) => s + r.rating, 0) / reviewCount : 0;
  const locationStr = [member.municipality, member.province].filter(Boolean).join(", ") || member.location;

  return (
    <div
      className="absolute left-3 right-3 z-40 rounded-2xl border border-border bg-card p-3 shadow-2xl"
      style={{ bottom: `${bottomOffset + 12}px` }}
    >
      <button
        onClick={onClose}
        className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground hover:bg-secondary"
        aria-label="Stäng"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <Avatar className="h-12 w-12 shrink-0">
          {member.avatar_url && <AvatarImage src={member.avatar_url} alt={member.full_name} />}
          <AvatarFallback className="text-xs">{getInitials(member.full_name)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="truncate text-sm font-bold text-foreground">{member.full_name}</p>
            {isVerified(member) && <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-primary" />}
          </div>
          {locationStr && <p className="truncate text-xs text-muted-foreground">{locationStr}</p>}
          <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
            {reviewCount > 0 ? (
              <span className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                {avgRating.toFixed(1)} ({reviewCount})
              </span>
            ) : (
              <span>Inga recensioner</span>
            )}
            <span>{listingsCount ?? 0} annonser</span>
          </div>
        </div>
      </div>
      <Button size="sm" className="mt-3 w-full" onClick={() => navigate(`/saljare/${member.username || member.id}`)}>
        Visa profil
      </Button>
    </div>
  );
}

const TOGGLE_BUTTON_HEIGHT = 56; // approx pill height + spacing

export default function MembersMobileLayout({
  members,
  isLoading,
  selectedMember,
  onSelectMember,
  search,
  onSearchChange,
  filters,
  onFiltersChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const total = members.length;

  const setChip = (key: "company" | "personal" | "verified") => {
    const next = { ...filters };
    if (key === "company") next.accountType = filters.accountType === "company" ? "all" : "company";
    else if (key === "personal") next.accountType = filters.accountType === "personal" ? "all" : "personal";
    else next.verified = filters.verified === "verified" ? "all" : "verified";
    onFiltersChange(next);
  };

  return (
    <>
      {/* Floating top: search + chips */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-30 px-3 pt-3">
        <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 shadow-lg">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            type="text"
            placeholder="Sök efter medlemmar..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{ fontSize: "16px" }}
            className="w-full bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          {search && (
            <button onClick={() => onSearchChange("")} className="text-muted-foreground" aria-label="Rensa sökning">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="pointer-events-auto mt-2 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <Chip active={filters.accountType === "company"} onClick={() => setChip("company")}>Företag</Chip>
          <Chip active={filters.accountType === "personal"} onClick={() => setChip("personal")}>Privatperson</Chip>
          <Chip active={filters.verified === "verified"} onClick={() => setChip("verified")}>Verifierade</Chip>
        </div>
      </div>

      {/* Sliding member list panel */}
      <div
        className="absolute inset-x-0 bottom-0 z-30 bg-card text-card-foreground shadow-[0_-8px_24px_rgba(0,0,0,0.18)]"
        style={{
          height: "90%",
          borderRadius: "20px 20px 0 0",
          transform: open ? "translateY(0)" : "translateY(100%)",
          transition: "transform 250ms ease-out",
        }}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <p className="text-sm font-bold text-foreground">
              {isLoading ? "Laddar..." : `${total} medlemmar`}
            </p>
            <button
              type="button"
              onClick={() => setFilterModalOpen(true)}
              className="flex items-center gap-1.5 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium text-foreground hover:bg-accent/10"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Filter
            </button>
          </div>

          <ScrollArea className="flex-1">
            <div className="px-2 pb-24">
              {isLoading ? (
                <p className="p-6 text-center text-sm text-muted-foreground">Laddar medlemmar...</p>
              ) : members.length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground">Inga medlemmar hittades.</p>
              ) : (
                members.map((m, idx) => (
                  <div key={m.id}>
                    <button
                      onClick={() => {
                        onSelectMember(m);
                        setOpen(false);
                      }}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg p-3 text-left transition-colors hover:bg-secondary",
                        selectedMember?.id === m.id && "bg-secondary"
                      )}
                    >
                      <Avatar className="h-11 w-11 shrink-0">
                        {m.avatar_url && <AvatarImage src={m.avatar_url} alt={m.full_name} />}
                        <AvatarFallback className="text-xs">{getInitials(m.full_name)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[15px] font-bold text-foreground">{m.full_name}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {[m.municipality, m.province].filter(Boolean).join(", ") || m.location || "—"}
                        </p>
                        {isCompany(m) && isVerified(m) && (
                          <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                            <BadgeCheck className="h-3 w-3" /> Verifierat företag
                          </span>
                        )}
                      </div>
                    </button>
                    {idx < members.length - 1 && <div className="mx-3 border-b border-border/60" />}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Toggle button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="absolute bottom-4 left-1/2 z-40 -translate-x-1/2 inline-flex items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-lg transition-colors hover:bg-primary/90 py-[12px] my-[10px]"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        {open ? "Dölj lista" : "Visa medlemmar"}
      </button>

      {/* Floating member detail card (only when list is closed) */}
      {selectedMember && !open && (
        <FloatingDetailCard
          member={selectedMember}
          bottomOffset={TOGGLE_BUTTON_HEIGHT + 16}
          onClose={() => onSelectMember(null)}
        />
      )}

      <MemberFilterModal
        open={filterModalOpen}
        onOpenChange={setFilterModalOpen}
        initial={filters}
        onApply={onFiltersChange}
      />
    </>
  );
}
